import {
  CategorySection,
  Decisions,
  DifferenceDecision,
  DifferenceRow,
  ReconciliationProposal,
} from './pdfReconciliationTypes';

/**
 * Display order for category accordions in the Review Differences step.
 * Singleton categories (PersonalInfo, Tax, PlanAssumptions) intermixed
 * with multi-item ones in the order an advisor typically reviews.
 *
 * Note: `personalInfoPrincipal` supports Keep/Replace only (never Add — a household has a
 * single principal). A possible client mismatch still surfaces as a banner warning at the
 * top of the review step so the advisor is alerted before choosing to Replace.
 */
export interface CategoryDef {
  key: keyof Omit<ReconciliationProposal, 'principalMismatchWarning'>;
  label: string;
  /** Singular noun used in row-level action copy. */
  entitySingular: string;
  /** Plural noun used in bulk-action copy when the count is not 1. */
  entityPlural: string;
  isSingleton: boolean;
  /**
   * Optional ProfileOPL controller name to deep-link to after apply
   * (used in the FinalizeImportStep success state).
   */
  profileLinkController?: string;
}

export const CATEGORIES: CategoryDef[] = [
  {
    key: 'personalInfoPrincipal',
    label: 'Principal',
    entitySingular: 'principal',
    entityPlural: 'principals',
    isSingleton: true,
    profileLinkController: 'Profile',
  },
  {
    key: 'personalInfoSpouse',
    label: 'Spouse',
    entitySingular: 'spouse',
    entityPlural: 'spouses',
    isSingleton: true,
    profileLinkController: 'Profile',
  },
  {
    key: 'accounts',
    label: 'Accounts',
    entitySingular: 'account',
    entityPlural: 'accounts',
    isSingleton: false,
    profileLinkController: 'ProfileAccounts',
  },
  {
    key: 'goals',
    label: 'Goals',
    entitySingular: 'goal',
    entityPlural: 'goals',
    isSingleton: false,
    profileLinkController: 'ProfileGoals',
  },
  {
    key: 'incomes',
    label: 'Incomes',
    entitySingular: 'income',
    entityPlural: 'incomes',
    isSingleton: false,
    profileLinkController: 'ProfileIncome',
  },
  {
    key: 'expenses',
    label: 'Expenses',
    entitySingular: 'expense',
    entityPlural: 'expenses',
    isSingleton: false,
    profileLinkController: 'ProfileExpenses',
  },
  {
    key: 'taxFiling',
    label: 'Tax filing',
    entitySingular: 'tax filing',
    entityPlural: 'tax filings',
    isSingleton: true,
    profileLinkController: 'ProfileTaxes',
  },
  {
    key: 'insurances',
    label: 'Insurance policies',
    entitySingular: 'insurance policy',
    entityPlural: 'insurance policies',
    isSingleton: false,
    profileLinkController: 'ProfileInsurance',
  },
  {
    key: 'otherAssets',
    label: 'Other assets',
    entitySingular: 'other asset',
    entityPlural: 'other assets',
    isSingleton: false,
    profileLinkController: 'ProfileOtherAssets',
  },
  {
    key: 'trusts',
    label: 'Trusts',
    entitySingular: 'trust',
    entityPlural: 'trusts',
    isSingleton: false,
    profileLinkController: 'Profile',
  },
  {
    key: 'dependents',
    label: 'Dependents',
    entitySingular: 'dependent',
    entityPlural: 'dependents',
    isSingleton: false,
    profileLinkController: 'Profile',
  },
  {
    key: 'planAssumptions',
    label: 'Plan assumptions',
    entitySingular: 'plan assumption',
    entityPlural: 'plan assumptions',
    isSingleton: true,
    profileLinkController: 'Profile',
  },
];

/** How often the wizard polls /Reconciliation while the item is still being analyzed (ms). */
export const POLL_INTERVAL_MS = 4000;

/**
 * A row is actionable when the advisor can pick a decision for it.
 * - ImportedOnly rows are always actionable (Add / Discard).
 * - Matched rows are actionable when the backend permits at least one override:
 *   - `allowOverride` (collection entries that aren't a row-level singleton) offers
 *     "Add as new", or
 *   - `allowOverwrite` (entity types supported by the replace handlers, including the
 *     Spouse and Tax filing singletons) offers "Replace existing".
 * - ExistingOnly rows are read-only.
 */
export const isActionable = (row: DifferenceRow): boolean =>
  row.kind === 'ImportedOnly' ||
  (row.kind === 'Matched' && (row.allowOverride || row.allowOverwrite));

/** The proposal's category sections, in display order, skipping any that are absent. */
const presentSections = (proposal: ReconciliationProposal): CategorySection[] =>
  CATEGORIES.map(
    def => proposal[def.key] as CategorySection | null | undefined,
  ).filter((section): section is CategorySection => Boolean(section));

/**
 * Returns true when every actionable row in every category has a decision selected.
 * Read-only rows are skipped because they cannot be acted upon.
 */
export const allDecisionsMade = (proposal: ReconciliationProposal, decisions: Decisions): boolean =>
  presentSections(proposal).every(section =>
    section.rows
      .filter(isActionable)
      .every(row => Boolean(decisions[section.categoryKey]?.[row.rowId])),
  );

/** Seed the decisions map from the proposal's defaultDecision values, actionable rows only. */
export const seedDecisions = (proposal: ReconciliationProposal): Decisions =>
  Object.fromEntries(
    presentSections(proposal).map(section => [
      section.categoryKey,
      Object.fromEntries(
        section.rows.filter(isActionable).map(row => [row.rowId, row.defaultDecision]),
      ),
    ]),
  );

/**
 * Counts rows in this section that contribute to the "N new" lozenge:
 * every actionable row whose advisor decision is "Add".
 */
export const sectionDifferenceCount = (section: CategorySection, decisions: Decisions): number => {
  const sectionDecisions = decisions[section.categoryKey] || {};
  return section.rows.filter(r => {
    if (!isActionable(r)) {
      return false;
    }
    const current = sectionDecisions[r.rowId] ?? r.defaultDecision;
    return current === 'Add';
  }).length;
};

/** Number of ImportedOnly rows in this section. */
export const countImportedOnlyRows = (section: CategorySection): number =>
  section.rows.filter(r => r.kind === 'ImportedOnly').length;

/**
 * Number of actionable Matched rows. When `overwriteOnly` is true, restricts to rows
 * the backend will accept for in-place replacement (`allowOverwrite`).
 */
export const countMatchedBulkRows = (
  section: CategorySection,
  options: { overwriteOnly?: boolean } = {},
): number =>
  section.rows.filter(
    r =>
      r.kind === 'Matched' && isActionable(r) && (options.overwriteOnly ? r.allowOverwrite : true),
  ).length;

/** Pick the singular or plural entity noun based on count. */
export const pluralizeEntity = (def: CategoryDef, count: number): string =>
  count === 1 ? def.entitySingular : def.entityPlural;

/**
 * Bulk-set `decision` on the section's rows matching `shouldUpdate`, returning a new
 * Decisions map. Rows that don't match keep whatever choice the advisor already made.
 */
const applyBulkDecision = (
  decisions: Decisions,
  section: CategorySection,
  decision: DifferenceDecision,
  shouldUpdate: (row: DifferenceRow) => boolean,
): Decisions => {
  const updates = Object.fromEntries(
    section.rows.filter(shouldUpdate).map(row => [row.rowId, decision]),
  );

  return {
    ...decisions,
    [section.categoryKey]: { ...decisions[section.categoryKey], ...updates },
  };
};

/**
 * Bulk-set the decision on every ImportedOnly row in a section. Matched and ExistingOnly
 * rows are untouched so the advisor's match-side choices are preserved.
 */
export const setImportedOnlyDecision = (
  decisions: Decisions,
  section: CategorySection,
  decision: 'Add' | 'Discard',
): Decisions => applyBulkDecision(decisions, section, decision, row => row.kind === 'ImportedOnly');

/**
 * Bulk-set the decision on actionable Matched rows. `Overwrite` only applies to rows where
 * the backend supports in-place replacement (`allowOverwrite`); other matched rows are
 * skipped. `Discard` applies to every actionable matched row.
 */
export const setMatchedDecision = (
  decisions: Decisions,
  section: CategorySection,
  decision: 'Overwrite' | 'Discard',
): Decisions =>
  applyBulkDecision(
    decisions,
    section,
    decision,
    row =>
      row.kind === 'Matched' &&
      isActionable(row) &&
      (decision === 'Overwrite' ? row.allowOverwrite : true),
  );

/**
 * Which side of the comparison the advisor's current decision accepts.
 * - `'import'`: the imported entity wins (Add or Overwrite).
 * - `'existing'`: the household stays unchanged for this row (Discard).
 * - `null`: read-only rows and rows with no decision yet.
 */
export const getAcceptedSide = (
  row: DifferenceRow,
  decision: DifferenceDecision | undefined,
): 'import' | 'existing' | null => {
  if (!isActionable(row) || !decision || decision === 'Pending') {
    return null;
  }
  if (decision === 'Add' || decision === 'Overwrite') {
    return 'import';
  }
  if (decision === 'Discard') {
    return 'existing';
  }
  return null;
};
